import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Star, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stars = ({ value = 0 }) => {
  const full = Math.round(value);
  return (
    <span>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= full ? "text-yellow-500" : "text-slate-300"}>
          ★
        </span>
      ))}
    </span>
  );
};

const ClientReviewsDialog = ({ isOpen, onOpenChange, clientId, onWriteReview }) => {
  const [requested, setRequested] = useState([]);   // leads with review_requested=true and review_submitted=false
  const [written, setWritten] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      // Fetch all leads of this client, then split
      const r = await axios.get(`${API}/business/leads/by-client/${clientId}`);
      const leads = r.data.leads || [];
      setRequested(leads.filter(l => l.review_requested && !l.review_submitted));
      // Fetch written reviews
      const r2 = await axios.get(`${API}/reviews/by-client/${clientId}`);
      setWritten(r2.data.reviews || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { if (isOpen) load(); /* eslint-disable-line */ }, [isOpen, clientId]);

  const reportResponse = async (reviewId) => {
    if (!window.confirm("Sei sicuro di voler segnalare la risposta dell'azienda?\n\nLa segnalazione verrà inviata all'amministratore di quandomiserve.it, che valuterà se eliminare la risposta o meno (es. in caso di minacce, offese o contenuto inappropriato).")) return;
    const reason = window.prompt("Indica brevemente il motivo della segnalazione (opzionale):", "") || "";
    try {
      await axios.post(`${API}/reviews/${reviewId}/report-response`, {
        client_id: clientId,
        reason: reason.trim(),
      });
      alert("Segnalazione inviata. L'amministratore la valuterà a breve.");
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore durante la segnalazione");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-orange-600">Le mie recensioni</DialogTitle>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2 mb-2">
          <p className="text-xs text-amber-800 leading-relaxed">
            ⚠️ <strong>Importante:</strong> Lascia una recensione <strong>solo se hai effettivamente noleggiato</strong> la piattaforma aerea. Recensioni non veritiere potranno essere rimosse dalla nostra moderazione.
          </p>
        </div>

        {loading ? (
          <p className="text-slate-500">Caricamento...</p>
        ) : (
          <>
            {/* Recensioni da scrivere */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Da scrivere ({requested.length})</h3>
              {requested.length === 0 ? (
                <p className="text-slate-500 text-sm bg-slate-50 border border-slate-200 rounded-lg p-4">
                  Nessuna richiesta di recensione in sospeso.
                </p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const occ = {};
                    requested.forEach(l => { const k = l.platform_title || "Piattaforma"; occ[k] = (occ[k] || 0) + 1; });
                    const idx = { ...occ };
                    return requested.map((lead) => {
                      const name = lead.platform_title || "Piattaforma";
                      const total = occ[name]; const ord = idx[name]; idx[name] = ord - 1;
                      const suffix = total > 1 ? ` (${ord})` : '';
                      return (
                    <div key={lead.id} className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`pending-review-${lead.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 text-sm">{name}{suffix}</p>
                        <p className="text-xs text-slate-500">
                          Contattata il {new Date(lead.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onWriteReview && onWriteReview(lead.id)}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid={`write-review-btn-${lead.id}`}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Scrivi recensione
                      </Button>
                    </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Recensioni già scritte */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Già scritte ({written.length})</h3>
              {written.length === 0 ? (
                <p className="text-slate-500 text-sm bg-slate-50 border border-slate-200 rounded-lg p-4">
                  Non hai ancora scritto recensioni.
                </p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const occ = {};
                    written.forEach(r => { occ[r.platform_title] = (occ[r.platform_title] || 0) + 1; });
                    const idx = { ...occ };
                    return written.map((r) => {
                      const name = r.platform_title;
                      const total = occ[name]; const ord = idx[name]; idx[name] = ord - 1;
                      const suffix = total > 1 ? ` (${ord})` : '';
                      return (
                    <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`written-review-${r.id}`}>
                      {r.is_suspended && (
                        <div className="mb-2 bg-purple-50 border border-purple-200 rounded p-2 text-xs text-purple-800">
                          <div className="font-semibold">🚫 La tua recensione è stata sospesa dalla moderazione</div>
                          {r.suspension_reason && <div className="mt-0.5"><span className="font-semibold">Motivo:</span> {r.suspension_reason}</div>}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-slate-800 text-sm">{name}{suffix}</p>
                        <Stars value={r.rating} />
                      </div>
                      <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        {new Date(r.created_at).toLocaleDateString('it-IT')}
                      </p>
                      {r.comment && <p className={`text-sm ${r.is_suspended ? 'text-slate-400 italic' : 'text-slate-700'}`}>{r.comment}</p>}
                      {r.owner_response && (
                        <div className={`mt-2 ml-3 pl-3 border-l-2 p-2 rounded ${r.owner_response_suspended ? 'border-purple-300 bg-purple-50' : 'border-orange-300 bg-orange-50'}`}>
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            <p className="text-xs font-semibold text-orange-700 flex items-center gap-2 flex-wrap">
                              Risposta dell'azienda:
                              {r.owner_response_suspended && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">🚫 Sospesa</span>
                              )}
                            </p>
                            {!r.owner_response_suspended && !r.is_suspended && (
                              <button
                                onClick={() => reportResponse(r.id)}
                                className="text-xs px-2 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50 font-medium"
                                title="Segnala questa risposta all'amministratore (minacce, offese, contenuto inappropriato)"
                                data-testid={`report-response-btn-${r.id}`}
                              >
                                ⚠ Segnala
                              </button>
                            )}
                          </div>
                          <p className={`text-sm ${r.owner_response_suspended ? 'text-slate-400 italic' : 'text-slate-700'}`}>{r.owner_response}</p>
                          {r.owner_response_suspended && r.owner_response_suspension_reason && (
                            <p className="mt-1 text-xs text-purple-700"><span className="font-semibold">Motivo:</span> {r.owner_response_suspension_reason}</p>
                          )}
                        </div>
                      )}
                    </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientReviewsDialog;
